/**
 * Itinerary Service - Handle itinerary CRUD operations
 * Integrates with backend API for planned and unplanned itineraries
 */

import { apiGet, apiPost, apiPut, apiDelete } from './apiClient';
import type { ItineraryDTO } from '../types/trip';

/**
 * Input for creating a planned itinerary (已排程的行程)
 */
export interface CreatePlannedItineraryInput {
  tripId: string;
  title: string;
  daySequence: number; // 第幾天（從 1 開始）
  startTime: string; // ISO 8601 格式: "2024-03-20T09:00:00"
  endTime: string; // ISO 8601 格式: "2024-03-20T12:00:00"
  locationName?: string;
  longitude?: number;
  latitude?: number;
  address?: string;
  placeId?: string;
  photoReference?: string;
  note?: string;
  type?: 'ACTIVITY' | 'STAY' | 'TRANSPORTATION' | 'MEAL';
}

/**
 * Input for creating an unplanned itinerary (未排程的行程)
 */
export interface CreateUnplannedItineraryInput {
  tripId: string;
  title: string;
  locationName?: string;
  longitude?: number;
  latitude?: number;
  address?: string;
  placeId?: string;
  photoReference?: string;
  note?: string;
  type?: 'ACTIVITY' | 'STAY' | 'TRANSPORTATION' | 'MEAL';
}

/**
 * Backend response for itinerary creation
 */
interface ItineraryResponse {
  exitCode: number;
  message: string;
  data: {
    id: string;
    title: string;
    tripId: number;
    daySequence?: number;
    orderInDay?: number;
    startTime?: string;
    endTime?: string;
    locationName?: string;
    longitude?: number;
    latitude?: number;
    address?: string;
    note?: string;
  };
}

/**
 * Create a planned itinerary (已排程的行程)
 */
export async function createPlannedItinerary(
  input: CreatePlannedItineraryInput
): Promise<void> {
  try {
    const response = await apiPost<ItineraryResponse>(
      '/trip/itinerary/planned',
      input
    );

    if (response.exitCode !== 1) {
      throw new Error(response.message || '建立行程失敗');
    }
  } catch (error) {
    console.error('Failed to create planned itinerary:', error);
    throw new Error('無法建立已排程的行程，請稍後再試');
  }
}

/**
 * Create an unplanned itinerary (未排程的行程)
 */
export async function createUnplannedItinerary(
  input: CreateUnplannedItineraryInput
): Promise<void> {
  try {
    const response = await apiPost<ItineraryResponse>(
      '/trip/itinerary/unplanned',
      input
    );

    if (response.exitCode !== 1) {
      throw new Error(response.message || '建立行程失敗');
    }
  } catch (error) {
    console.error('Failed to create unplanned itinerary:', error);
    throw new Error('無法建立未排程的行程，請稍後再試');
  }
}

/**
 * Input for updating an itinerary
 */
export interface UpdateItineraryInput {
  itineraryId: string;
  title?: string;
  startTime?: string;
  endTime?: string;
  locationName?: string;
  longitude?: number;
  latitude?: number;
  address?: string;
  placeId?: string;
  photoReference?: string;
  note?: string;
  type?: 'ACTIVITY' | 'STAY' | 'TRANSPORTATION' | 'MEAL';
}

/**
 * Update an itinerary (更新行程)
 */
export async function updateItinerary(
  input: UpdateItineraryInput
): Promise<void> {
  try {
    const { itineraryId, ...updateData } = input;
    const response = await apiPut<ItineraryResponse>(
      `/trip/itinerary/${itineraryId}`,
      updateData
    );

    if (response.exitCode !== 1) {
      throw new Error(response.message || '更新行程失敗');
    }
  } catch (error) {
    console.error('Failed to update itinerary:', error);
    throw new Error('無法更新行程，請稍後再試');
  }
}

/**
 * Backend response for itinerary deletion
 */
interface DeleteItineraryResponse {
  exitCode: number;
  message: string;
  data: {
    itineraryId: string;
  };
}

/**
 * Delete an itinerary (刪除行程)
 */
export async function deleteItinerary(itineraryId: string): Promise<void> {
  try {
    const response = await apiDelete<DeleteItineraryResponse>(
      `/trip/itinerary/${itineraryId}`
    );

    if (response.exitCode !== 1) {
      throw new Error(response.message || '刪除行程失敗');
    }
  } catch (error) {
    console.error('Failed to delete itinerary:', error);
    throw new Error('無法刪除行程，請稍後再試');
  }
}

/**
 * Input for batch reordering itineraries
 */
export interface ReorderItinerariesInput {
  tripId: string;
  daySequence: number; // 1-based day number
  itineraryOrders: Array<{
    itineraryId: string;
    orderInDay: number; // Must be consecutive starting from 1
  }>;
}

/**
 * Backend response for batch reorder
 */
interface ReorderItinerariesResponse {
  exitCode: number;
  message: string;
}

/**
 * Batch reorder itineraries for a specific day (批量更新行程順序)
 */
export async function reorderItineraries(
  input: ReorderItinerariesInput
): Promise<void> {
  try {
    const { tripId, ...requestBody } = input;
    const response = await apiPut<ReorderItinerariesResponse>(
      `/trip/${tripId}/itineraries/reorder`,
      requestBody
    );

    if (response.exitCode !== 1) {
      throw new Error(response.message || '更新行程順序失敗');
    }
  } catch (error) {
    console.error('Failed to reorder itineraries:', error);
    throw new Error('無法更新行程順序，請稍後再試');
  }
}

/**
 * Input for updating an itinerary's day sequence
 */
export interface UpdateDaySequenceInput {
  itineraryId: string;
  tripId: string;
  daySequence: number; // 第幾天（從 1 開始）
}

/**
 * Update an itinerary's day sequence (移動行程至不同天)
 */
export async function updateItineraryDaySequence(
  input: UpdateDaySequenceInput
): Promise<void> {
  try {
    const { itineraryId, tripId, daySequence } = input;
    const response = await apiPut<ItineraryResponse>(
      `/trip/itinerary/${itineraryId}/daySequence`,
      { tripId, daySequence }
    );

    if (response.exitCode !== 1) {
      throw new Error(response.message || '移動行程失敗');
    }
  } catch (error) {
    console.error('Failed to update itinerary day sequence:', error);
    throw new Error('無法移動行程，請稍後再試');
  }
}

/**
 * Input for setting an itinerary's primary flag
 */
export interface SetPrimaryInput {
  itineraryId: string;
  tripId: string;
  isPrimary: boolean;
}

/**
 * Set whether an itinerary is the primary one for its time slot (標記/取消主要行程)
 */
export async function setItineraryPrimary(input: SetPrimaryInput): Promise<void> {
  try {
    const { itineraryId, tripId, isPrimary } = input;
    const response = await apiPut<ItineraryResponse>(
      `/trip/itinerary/${itineraryId}/primary`,
      { tripId, isPrimary }
    );

    if (response.exitCode !== 1) {
      throw new Error(response.message || '設定主要行程失敗');
    }
  } catch (error) {
    console.error('Failed to set itinerary primary:', error);
    throw new Error('無法設定主要行程，請稍後再試');
  }
}

/**
 * Get itinerary detail (取得行程詳情)
 */
export async function getItineraryDetail(itineraryId: string): Promise<ItineraryDTO> {
  const response = await apiGet<{ exitCode: number; message: string; data: ItineraryDTO }>(
    `/trip/itinerary/${itineraryId}`
  );
  if (response.exitCode !== 1 || !response.data) {
    throw new Error(response.message || '取得行程詳情失敗');
  }
  return response.data;
}

/**
 * Input for parsing a URL into place info (no itinerary is created)
 */
export interface ParseUrlInput {
  tripId: string;
  url: string;
}

export interface ParsedPlace {
  title: string;
  locationName: string;
  address: string;
  latitude: number;
  longitude: number;
  placeId: string | null;
  photoReference: string | null;
}

/**
 * Parse a Google Maps or Instagram URL into place info, without creating an itinerary.
 * Caller is responsible for creating the itinerary afterwards (e.g. via createPlanned/createUnplanned)
 * once the user has confirmed details such as the itinerary type.
 */
export async function parseItineraryUrl(
  input: ParseUrlInput
): Promise<ParsedPlace> {
  const response = await apiPost<{ exitCode: number; message: string; data: ParsedPlace }>(
    '/trip/itinerary/parse-url',
    input
  );

  if (response.exitCode !== 1) {
    throw new Error(response.message || '解析連結失敗');
  }

  return response.data;
}

export const itineraryService = {
  createPlanned: createPlannedItinerary,
  createUnplanned: createUnplannedItinerary,
  update: updateItinerary,
  delete: deleteItinerary,
  reorder: reorderItineraries,
  updateDaySequence: updateItineraryDaySequence,
  setPrimary: setItineraryPrimary,
  getDetail: getItineraryDetail,
  parseUrl: parseItineraryUrl,
};
